/* Business Onboarding Funnel — Phase 3
 *
 * Screens B-1..B-5 of the Customer Journey Redesign v3. Uses the existing
 * Signup.jsx design language (bg-white, font-display, btn-primary, HX tokens)
 * so no new visual language is introduced.
 *
 * Flow:
 *   /start/business/seats           → pick seat count
 *   /start/business/account         → account details
 *   /start/business/kyc             → Persona/Stripe-Identity verification
 *   /start/business/provisioning    → post-payment polling until org.status = active
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import {
  ArrowRight, ArrowLeft, Buildings, User, CheckCircle,
  ShieldCheck, CreditCard, IdentificationCard,
} from "@phosphor-icons/react";

const STEPS = ["Communication Seats", "Account details", "KYC verification", "Payment", "Setting up"];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SEAT_PRESETS = [1, 5, 10, 25, 50, 100];

export default function BusinessFunnel() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [step, setStep] = useState(0);
  // The gated funnel handles both business + individual paths. URL params
  // (?type=business|individual&plan=<plan_key>) drive which shape is POSTed
  // to /api/onboarding/intent — this closes the free-signup loophole for
  // individuals hitting /start/business?type=individual from the pricing tiles.
  const accountType = (params.get("type") === "individual") ? "individual" : "business";
  const planKey = params.get("plan") || (accountType === "individual" ? "individual" : "business");

  // Warm the CSRF cookie eagerly — the first POST /onboarding/intent lives
  // ~3 clicks in, and some browsers race the axios interceptor's
  // just-in-time cookie fetch with the actual request. Fetching here means
  // the cookie is already in document.cookie before the user clicks Continue.
  useEffect(() => {
    api.get("/auth/csrf").catch(() => { /* handled at request time */ });
  }, []);
  const [seats, setSeats] = useState(5);
  const [form, setForm] = useState({
    org_name: "", admin_name: "", admin_email: "", phone: "",
    country: "US", admin_password: "",
  });
  const [intent, setIntent] = useState(null);   // {intent_id, status?, ...}
  const [kyc, setKyc] = useState(null);         // {verification_url, session_id, ...}
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [pricing, setPricing] = useState(null); // {per_seat_display, currency}

  // Recover intent_id from URL (customer bouncing back from Stripe / KYC).
  // We also mirror the intent_id into localStorage on every mutation below so
  // that if Veriff / Stripe redirects lose the ?intent= param (e.g. when
  // APP_BASE_URL isn't set on the backend and the callback URL becomes
  // relative), we can still restore state on next mount.
  useEffect(() => {
    const iidFromUrl = params.get("intent");
    const iidFromStorage = (typeof window !== "undefined")
      ? window.localStorage.getItem("hbf.funnel.intent_id") : null;
    const iid = iidFromUrl || iidFromStorage;
    // Detect the post-payment redirect from Stripe. success_url is
    // `/start/business/provisioning?intent=<id>` — the presence of
    // `provisioning` in the pathname tells us the customer has paid and is
    // waiting on the webhook to activate the org. In that case we MUST land
    // at Step 4 (Setting up) and poll — otherwise a slow webhook would
    // bounce them back to the Payment step to pay again.
    const isPostPayment = (typeof window !== "undefined")
      && window.location.pathname.includes("/provisioning");
    if (iid && !intent) {
      (async () => {
        try {
          const { data } = await api.get(`/onboarding/intent/${iid}`);
          setIntent({ intent_id: iid, ...data });
          // Sync URL to include the intent id so refresh keeps state.
          if (!iidFromUrl) {
            const url = new URL(window.location.href);
            url.searchParams.set("intent", iid);
            window.history.replaceState({}, "", url.toString());
          }
          if (data.stage === "activated") { setStep(4); return; }
          if (data.subscription_status === "active") { setStep(4); return; }
          // Post-payment landing overrides everything else — poll until the
          // webhook flips the org active.
          if (isPostPayment) { setStep(4); return; }
          if (data.stage === "ready_for_checkout") { setStep(3); return; }
          if (data.stage === "kyc_in_progress" || data.stage === "kyc_needs_more_documents") {
            setStep(2); return;
          }
          if (data.stage === "kyc_rejected") { setStep(2); setErr("Your KYC submission was rejected. Contact support if this is a mistake."); return; }
          setStep(2); // awaiting_kyc
        } catch {
          // Stale intent — clear the localStorage cache so the customer can
          // start over instead of getting stuck in a loop.
          try { window.localStorage.removeItem("hbf.funnel.intent_id"); } catch (_) { /* noop */ }
        }
      })();
    }
  }, [params, intent]);

  // Load canonical Business per-seat price
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/public/pricing?currency=usd`, { credentials: "omit" });
        const j = await r.json();
        const biz = (j.products || []).find((p) => p.plan_key === "business");
        if (biz?.price) setPricing({
          per_seat_display: biz.price.display,
          per_seat_amount:  biz.price.amount_minor / 100,
          currency:         biz.price.currency || "usd",
        });
      } catch { /* offline — show fallback below */ }
    })();
  }, []);

  const total = useMemo(() => {
    const perSeat = pricing?.per_seat_amount || 38;
    return `$${(perSeat * seats).toFixed(2)}`;
  }, [pricing, seats]);

  const canNext1 =
    form.org_name.trim() && form.admin_name.trim() &&
    form.admin_email.includes("@") && form.admin_password.length >= 6 &&
    form.country.length === 2;

  // ── Step 1 → 2: create/resume the Signup Intent ──────────────────────────
  async function submitAccount() {
    setLoading(true); setErr("");
    try {
      // Belt-and-suspenders: warm the CSRF cookie right before the POST.
      // The axios interceptor also does this, but calling it here removes
      // any race between the interceptor's just-in-time fetch and the
      // browser's cookie-jar update.
      try { await api.get("/auth/csrf"); } catch { /* fall through */ }
      const { data } = await api.post("/onboarding/intent", {
        account_type: accountType, plan: planKey,
        org_name: form.org_name.trim(),
        admin_name: form.admin_name.trim(),
        admin_email: form.admin_email.trim(),
        admin_password: form.admin_password,
        country: form.country, seats: accountType === "individual" ? 1 : seats,
      });
      const intentId = data.intent_id;
      setParams({ intent: intentId });
      try { window.localStorage.setItem("hbf.funnel.intent_id", intentId); } catch (_) { /* noop */ }

      // Resume-aware: same email + password may have returned an existing
      // intent that is already past KYC (customer bouncing back from a
      // different browser / incognito / a week later). Inspect the current
      // stage and jump the user to the earliest incomplete step so they
      // never redo KYC or seat selection.
      let stage = "awaiting_kyc";
      try {
        const s = await api.get(`/onboarding/intent/${intentId}`);
        stage = s.data.stage || "awaiting_kyc";
        setIntent({ intent_id: intentId, ...s.data });
      } catch {
        setIntent({ intent_id: intentId, stage });
      }
      if (stage === "activated" || stage === "payment_confirmed_awaiting_activation") {
        setStep(4);   // Setting up / activated
      } else if (stage === "ready_for_checkout") {
        setStep(3);   // Payment step — skip KYC, it's done
      } else if (stage === "kyc_rejected") {
        setStep(2); setErr("Your previous KYC submission was rejected. Please try again or contact support.");
      } else {
        setStep(2);   // awaiting_kyc / kyc_in_progress / kyc_needs_more_documents
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
    setLoading(false);
  }

  // ── Step 2: start KYC session ────────────────────────────────────────────
  async function startKyc() {
    setLoading(true); setErr("");
    try {
      const { data } = await api.post(`/onboarding/intent/${intent.intent_id}/kyc`);
      setKyc(data);
      if (data.verification_url) {
        // Open in same tab — Persona will redirect back with intent_id preserved
        window.location.href = data.verification_url;
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
    setLoading(false);
  }

  // ── Step 3: create Stripe Checkout (P0 gate: 400 if KYC not verified) ────
  async function launchCheckout() {
    setLoading(true); setErr("");
    try {
      const { data } = await api.post(`/onboarding/intent/${intent.intent_id}/checkout`, {});
      window.location.href = data.checkout_url;
    } catch (e) {
      // Surface more diagnostic detail — HTTP status + any body — so operators
      // can spot config issues (missing Stripe key, wrong price, etc) without
      // needing browser dev tools open.
      const status = e.response?.status;
      const raw = e.response?.data;
      const detail = (raw && typeof raw === "object") ? (raw.detail ?? raw.message ?? JSON.stringify(raw)) : raw;
      const shown = detail
        ? `${status ? `[${status}] ` : ""}${formatApiError(detail)}`
        : (status
            ? `Stripe checkout failed (HTTP ${status}). Verify STRIPE_SECRET_KEY and Business plan pricing in Owner Console → Environment → Payments.`
            : (e.message || "Something went wrong."));
      setErr(shown);
      setLoading(false);
    }
  }

  // ── Step 4: poll until webhook flips the org to active ───────────────────
  useEffect(() => {
    if (step !== 4 || !intent?.intent_id) return;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/onboarding/intent/${intent.intent_id}`);
        if (data.status === "active" || data.stage === "activated") {
          clearInterval(iv);
          // Redirect to login — the wizard triggers automatically post-login
          nav("/login?onboarding=1");
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(iv);
  }, [step, intent, nav]);

  return (
    <div className="min-h-screen bg-white" data-testid="business-funnel">
      <header className="border-b border-[var(--border)] px-8 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5"><Logo size={36} /></Link>
        <div className="text-[13px] text-[var(--text-secondary)]">Have an account? <Link to="/login" className="font-semibold text-[var(--accent)]">Log in</Link></div>
      </header>

      <div className="max-w-3xl mx-auto px-8 py-12">
        <div className="flex items-center justify-between mb-12" data-testid="funnel-stepper">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1 flex items-center">
              <div className={`w-7 h-7 flex items-center justify-center font-mono text-[12px] font-bold ${i < step ? "bg-[var(--success)] text-white" : i === step ? "bg-[var(--accent)] text-white" : "bg-[var(--bg-sidebar)] text-[var(--text-muted)] border border-[var(--border)]"}`}>
                {i < step ? <CheckCircle size={14} weight="fill" /> : i + 1}
              </div>
              <div className={`ml-2 text-[11px] font-semibold tracking-wider uppercase ${i === step ? "text-[var(--text-primary)]" : "text-[var(--text-muted)]"} hidden sm:inline`}>{s}</div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-4 ${i < step ? "bg-[var(--success)]" : "bg-[var(--border)]"}`} />}
            </div>
          ))}
        </div>

        {/* ── Step 0: Choose seats ────────────────────────────────────── */}
        {step === 0 && (
          <div data-testid="step-seats">
            <div className="font-mono text-[11px] tracking-widest text-[var(--accent)] mb-3">/ STEP 1 · HELLO BUSINESS</div>
            <h1 className="font-display font-black text-[40px] leading-[1.0] mb-3">How many Communication<br />Seats will you need?</h1>
            <p className="text-[14px] text-[var(--text-secondary)] mb-10">
              Each seat includes one license, one business number, and one employee assignment.
              You can add more later.
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-6">
              {SEAT_PRESETS.map((n) => (
                <button key={n} onClick={() => setSeats(n)} data-testid={`seat-preset-${n}`}
                  className={`py-5 border font-display font-bold text-[24px] transition-colors ${seats === n ? "border-[var(--accent)] bg-[var(--bg-sidebar)] text-[var(--accent)]" : "border-[var(--border)] hover:bg-[var(--bg-sidebar)]"}`}>
                  {n}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3 mb-8">
              <label className="text-[13px] text-[var(--text-secondary)]">Custom:</label>
              <input type="number" min="1" max="10000" value={seats}
                onChange={(e) => setSeats(Math.max(1, Math.min(10000, Number(e.target.value) || 1)))}
                className="input w-32" data-testid="seat-custom" />
            </div>
            <div className="border border-[var(--border)] p-5 mb-8 bg-[var(--bg-sidebar)]">
              <div className="text-[12px] text-[var(--text-secondary)] uppercase font-mono tracking-wider mb-1">Monthly total</div>
              <div className="font-display font-black text-[32px]" data-testid="seat-total">{total}</div>
              <div className="text-[13px] text-[var(--text-secondary)] mt-1">
                {seats} × {pricing?.per_seat_display || "$38.00"} per Communication Seat / month
              </div>
            </div>
            <div className="flex justify-end">
              <button onClick={() => setStep(1)} className="btn-primary inline-flex items-center gap-2" data-testid="seats-next">
                Continue <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* ── Step 1: Account details ─────────────────────────────────── */}
        {step === 1 && (
          <div data-testid="step-account">
            <div className="font-mono text-[11px] tracking-widest text-[var(--accent)] mb-3">/ STEP 2 · ACCOUNT DETAILS</div>
            <h1 className="font-display font-black text-[40px] leading-[1.0] mb-3">Tell us about<br />your business.</h1>
            <p className="text-[14px] text-[var(--text-secondary)] mb-10">This is the account that will own the {seats} Communication {seats === 1 ? "Seat" : "Seats"}.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Company name" testid="acc-org_name" value={form.org_name} onChange={(v) => setForm({ ...form, org_name: v })} />
              <Field label="Admin full name" testid="acc-admin_name" value={form.admin_name} onChange={(v) => setForm({ ...form, admin_name: v })} />
              <Field label="Work email" testid="acc-admin_email" value={form.admin_email} onChange={(v) => setForm({ ...form, admin_email: v })} type="email" />
              <Field label="Phone (optional)" testid="acc-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
              <div>
                <label className="label">Country</label>
                <select className="input" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="acc-country">
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="GB">United Kingdom</option>
                  <option value="IN">India</option>
                </select>
              </div>
              <Field label="Password (min 6)" testid="acc-admin_password" value={form.admin_password} onChange={(v) => setForm({ ...form, admin_password: v })} type="password" />
            </div>
            {err && <div className="mt-6 text-[13px] text-[var(--danger)]" data-testid="acc-error">{err}</div>}
            <div className="mt-10 flex justify-between">
              <button onClick={() => setStep(0)} className="btn-secondary inline-flex items-center gap-2"><ArrowLeft size={16} /> Back</button>
              <button disabled={!canNext1 || loading} onClick={submitAccount} className="btn-primary inline-flex items-center gap-2 disabled:opacity-40" data-testid="acc-next">
                {loading ? "Saving…" : "Continue"} <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: KYC ─────────────────────────────────────────────── */}
        {step === 2 && (
          <div data-testid="step-kyc">
            <div className="font-mono text-[11px] tracking-widest text-[var(--accent)] mb-3">/ STEP 3 · KYC VERIFICATION</div>
            <h1 className="font-display font-black text-[40px] leading-[1.0] mb-3">Verify your business,<br />then we'll take payment.</h1>
            <p className="text-[14px] text-[var(--text-secondary)] mb-10">
              We verify every business account before charging you. This protects your customers
              and keeps the platform compliant. No payment will be taken until verification passes.
            </p>
            <div className="border border-[var(--border)] p-6 mb-6 flex items-start gap-4">
              <ShieldCheck size={28} weight="duotone" className="text-[var(--accent)] mt-1" />
              <div>
                <div className="font-display font-bold text-[18px] mb-1">Business identity verification</div>
                <div className="text-[13px] text-[var(--text-secondary)] leading-relaxed">
                  You'll be redirected to our verification partner. Have a photo ID and
                  proof of company registration ready. Most reviews complete in under a minute.
                </div>
              </div>
            </div>
            {err && <div className="mb-6 text-[13px] text-[var(--danger)]" data-testid="kyc-error">{err}</div>}
            <div className="flex justify-between">
              <div className="text-[12px] text-[var(--text-muted)] uppercase font-mono tracking-wider self-center">
                Intent · {intent?.intent_id?.slice(0, 8)}…
              </div>
              <button disabled={loading} onClick={startKyc} className="btn-primary inline-flex items-center gap-2 disabled:opacity-40" data-testid="kyc-start">
                {loading ? "Starting…" : "Begin verification"} <ArrowRight size={16} />
              </button>
            </div>
            <div className="mt-8 text-[12px] text-[var(--text-muted)]">
              Already verified? <button className="font-semibold text-[var(--accent)] hover:underline" onClick={() => setStep(3)} data-testid="kyc-skip-to-payment">Continue to payment →</button>
            </div>
          </div>
        )}

        {/* ── Step 3: Payment ─────────────────────────────────────────── */}
        {step === 3 && (
          <div data-testid="step-payment">
            <div className="font-mono text-[11px] tracking-widest text-[var(--accent)] mb-3">/ STEP 4 · PAYMENT</div>
            <h1 className="font-display font-black text-[40px] leading-[1.0] mb-3">Almost there —<br />secure your seats.</h1>
            <p className="text-[14px] text-[var(--text-secondary)] mb-10">You'll be redirected to Stripe to complete payment. Your organization is created only after successful payment.</p>
            <div className="border border-[var(--border)] p-6 mb-6">
              <div className="flex justify-between mb-2">
                <span className="text-[13px]">Business plan × {seats} {seats === 1 ? "seat" : "seats"}</span>
                <span className="font-display font-bold text-[16px]" data-testid="pay-total">{total} / mo</span>
              </div>
              <div className="text-[12px] text-[var(--text-muted)]">Billed monthly. Cancel anytime.</div>
            </div>
            {err && <div className="mb-6 text-[13px] text-[var(--danger)]" data-testid="pay-error">{err}</div>}
            <div className="flex justify-between">
              <button onClick={() => setStep(2)} className="btn-secondary inline-flex items-center gap-2"><ArrowLeft size={16} /> Back</button>
              <button disabled={loading} onClick={launchCheckout} className="btn-primary inline-flex items-center gap-2 disabled:opacity-40" data-testid="pay-checkout">
                <CreditCard size={16} /> {loading ? "Redirecting…" : "Continue to Stripe"}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 4: Provisioning polling ────────────────────────────── */}
        {step === 4 && (
          <div data-testid="step-provisioning" className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-6 border-4 border-[var(--accent)] border-t-transparent rounded-full animate-spin" />
            <div className="font-mono text-[11px] tracking-widest text-[var(--accent)] mb-3">/ STEP 5 · SETTING UP</div>
            <h1 className="font-display font-black text-[36px] leading-[1.0] mb-3">Provisioning your workspace…</h1>
            <p className="text-[14px] text-[var(--text-secondary)] max-w-md mx-auto">
              Payment confirmed. Creating your organization and preparing {seats} Communication {seats === 1 ? "Seat" : "Seats"}.
              You'll be redirected to log in shortly.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="input w-full" data-testid={testid} />
    </div>
  );
}
